import {none, some} from '@danwang/option';
import {useSplitRawTransactionsMutation} from '@transactions/graphql';
import ArrayUtils from 'common/array';
import {parseDate} from 'common/date';
import Money from 'common/Money';
import {ChevronRight, Plus, X} from 'lucide-react';
import * as React from 'react';
import {Button} from 'src/Button';
import DatePicker from 'src/DatePicker/DatePicker';
import {useMoneyFormatter, useUnitsById} from 'src/hooks/useUnits';
import InputGroup from 'src/InputGroup';
import AmountInput from 'src/inputs/AmountInput';
import {TagsAndFieldsInput} from 'src/inputs/TagsAndFieldsInput/TagsAndFieldsInput';
import {
  currencyLabelWithUnits,
  displayUnitHundredthsWithUnits,
  formatMoneyWithUnits,
} from 'src/lib/unitDisplay';
import {Modal} from 'src/Modal';
import {
  CopyToAllButton,
  PaymentTransactionFields,
} from 'src/PaymentTransactionFields';
import type {Option} from '@danwang/option';
import type {BasicRawTransactionFragment, SplitInput} from '@transactions/graphql';
import type {DateTime} from 'luxon';
import type {SystemProps} from 'src/Modals';
import type {CopyableField} from 'src/PaymentTransactionFields';

type PartialSplitRow = {
  // Stable identity for React keys and the expanded-rows set, so deleting a
  // row doesn't shift state onto its neighbor.
  id: number;
  amount: Option<Money>;
  payee: string;
  category: Option<string>;
  description: string;
  date: DateTime;
  // Tag names and field ID -> value map for this split, matching the shapes
  // TagsAndFieldsInput reads and the SplitInput mutation payload expects.
  tags: string[];
  fieldValues: Record<string, string>;
};

const emptyRow = (
  id: number,
  amount: Option<Money>,
  date: DateTime,
): PartialSplitRow => ({
  id,
  amount,
  payee: '',
  category: none,
  description: '',
  date,
  tags: [],
  fieldValues: {},
});

export type SplitRawTransactionModalUserProps = {
  // One or more raw transactions sharing a currency. With several, each split
  // consumes every raw proportionally to its share of the combined net.
  rawTransactions: BasicRawTransactionFragment[];
  onSplit: (rawTransactions: BasicRawTransactionFragment[]) => void;
};

type SplitRowCardProps = {
  index: number;
  value: PartialSplitRow;
  currency: string;
  expanded: boolean;
  onToggle: () => void;
  onChange: (row: PartialSplitRow) => void;
  onCopyToAll: (field: CopyableField) => void;
  onDelete: () => void;
};

const SplitRowCard = ({
  index,
  value,
  currency,
  expanded,
  onToggle,
  onChange,
  onCopyToAll,
  onDelete,
}: SplitRowCardProps) => {
  const formatMoney = useMoneyFormatter();
  const summary = [
    value.amount.map((m) => formatMoney(m)).getOrElse('no amount'),
    value.date.toFormat('MMM d'),
    ...(value.payee ? [value.payee] : []),
  ].join(' · ');

  return (
    <div className="border-border rounded-lg border">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => {
          // Only toggle for keys on the header itself — Enter/Space on the
          // nested delete button bubbles here and must activate the button,
          // not toggle the card.
          if (e.target !== e.currentTarget) {
            return;
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        className="flex cursor-pointer items-center gap-2 px-4 py-3 select-none"
      >
        <ChevronRight
          className="text-muted-foreground size-4 shrink-0 transition-transform duration-100"
          style={{transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)'}}
        />
        <span className="shrink-0 text-sm font-medium">Split {index + 1}</span>
        <span className="text-muted-foreground min-w-0 truncate text-sm">
          {summary}
        </span>
        {!expanded && value.category.isEmpty && (
          <span className="text-destructive shrink-0 text-xs">
            no category
          </span>
        )}
        <div className="flex-1" />
        <div onClick={(e) => e.stopPropagation()}>
          <Button
            variant="ghost"
            size="icon"
            type="button"
            onClick={onDelete}
            className="shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
      {expanded && (
        <div className="flex flex-col gap-3 px-4 pb-4">
          {/* Stacked on mobile: side-by-side halves leave no room for the
              input next to InputGroup's fixed-width label. */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="sm:flex-1">
              <InputGroup label="Amount">
                <AmountInput
                  value={value.amount.map((m) =>
                    m.currency === currency
                      ? m
                      : Money.fromInt(m.amount, currency),
                  )}
                  onChange={(amount) =>
                    onChange({
                      ...value,
                      amount: amount.map((m) =>
                        Money.fromInt(m.amount, currency),
                      ),
                    })
                  }
                  currency={currency}
                />
              </InputGroup>
            </div>
            <div className="sm:flex-1">
              <InputGroup label="Date">
                <DatePicker
                  selectedDate={value.date}
                  onChange={(date) => onChange({...value, date})}
                />
              </InputGroup>
            </div>
          </div>
          <PaymentTransactionFields
            value={{
              payee: value.payee,
              category: value.category,
              description: value.description,
            }}
            onChange={({payee, category, description}) =>
              onChange({...value, payee, category, description})
            }
            onCopyToAll={onCopyToAll}
          />
          <InputGroup label="Tags & fields">
            <div className="flex items-start gap-1">
              <div className="flex-1">
                <TagsAndFieldsInput
                  tags={value.tags}
                  fieldValues={value.fieldValues}
                  onChange={(tags, fieldValues) =>
                    onChange({...value, tags, fieldValues})
                  }
                />
              </div>
              <CopyToAllButton onClick={() => onCopyToAll('tagsAndFields')} />
            </div>
          </InputGroup>
        </div>
      )}
    </div>
  );
};

export const SplitRawTransactionModal = (
  props: SplitRawTransactionModalUserProps & SystemProps,
) => {
  const formId = React.useId();
  const unitsById = useUnitsById();
  const {rawTransactions, onRequestClose} = props;

  const currency = rawTransactions[0].currency;
  const netTotal = Money.fromInt(
    rawTransactions.reduce((sum, r) => sum + r.amount, 0),
    currency,
  );
  const total = Money.fromInt(Math.abs(netTotal.amount), currency);
  const earliestDate = parseDate(
    rawTransactions.map((r) => r.date).reduce((a, b) => (a <= b ? a : b)),
  );

  const [pending, setPending] = React.useState(false);
  const nextIdRef = React.useRef(1);
  // Start with a single zero-amount split. The intended flow is to type an
  // amount into the first split, then "Add split" to auto-fill the remainder
  // onto the second. Submitting still requires at least two splits.
  const [rows, setRows] = React.useState<PartialSplitRow[]>(() => [
    emptyRow(0, some(Money.zero(currency)), earliestDate),
  ]);
  // Only the first split starts expanded; the rest collapse to summary rows
  // so the modal stays scannable, especially on mobile.
  const [expandedIds, setExpandedIds] = React.useState<ReadonlySet<number>>(
    () => new Set([0]),
  );

  const handleToggleRow = (id: number) => () => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const allocated = rows
    .map((r) => r.amount.getOrElse(Money.zero(currency)))
    .reduce((a, b) => Money.plus(a, b), Money.zero(currency));
  const remaining = Money.minus(total, allocated);

  const remainingColorClass = remaining.isZero
    ? 'text-foreground'
    : remaining.isNegative
      ? 'text-destructive'
      : 'text-muted-foreground';

  const allPositive = rows.every((r) =>
    r.amount.map((m) => m.isPositive).getOrElse(false),
  );
  const amountsComplete = allPositive && remaining.isZero;

  // Every split becomes a payment transaction, which requires a category, so a
  // row only yields a payload when it has both an amount and a category.
  const maybePayload: Option<SplitInput[]> = ArrayUtils.allOptionArray(
    rows.map((row) =>
      row.amount.flatMap((m) =>
        row.category.map((categoryName) => ({
          amount: Math.abs(m.amount),
          payee: row.payee || undefined,
          categoryName,
          tagNames: row.tags.length > 0 ? row.tags : undefined,
          fieldValues:
            Object.keys(row.fieldValues).length > 0
              ? row.fieldValues
              : undefined,
          description: row.description || undefined,
          pending: pending || undefined,
          date: row.date.toISODate(),
        })),
      ),
    ),
  );

  const [splitMutation, {loading}] = useSplitRawTransactionsMutation();

  // A zero-decimal currency cannot express a sub-unit total, so a legacy raw
  // stored as e.g. ¥100.50 has no valid split: every row the generators
  // produce carries the residue and is rejected by the server's
  // whole-display-unit check. Block it up front with a reason rather than
  // offering buttons whose output can never submit.
  const unsplittableResidue =
    total.amount % displayUnitHundredthsWithUnits(currency, unitsById) !== 0;

  // A split must produce at least two transactions to be meaningful, so block
  // submitting while fewer than two rows exist even if their amounts balance.
  const canSubmit =
    !loading &&
    rows.length >= 2 &&
    amountsComplete &&
    !unsplittableResidue &&
    !maybePayload.isEmpty;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Guard against submits that bypass the disabled button (e.g. Enter key):
    // don't create anything until the amounts balance and every split has a
    // category.
    if (!canSubmit) {
      return;
    }
    maybePayload.forEach((splits) => {
      splitMutation({
        variables: {
          rawTransactionIds: rawTransactions.map((r) => r._id),
          splits,
          // Every split carries its own date; this top-level default never
          // applies, but the mutation requires it.
          date: earliestDate.toISODate(),
        },
      }).then((result) => {
        if (result.data?.splitRawTransaction) {
          props.onSplit(rawTransactions);
          onRequestClose();
        }
      });
    });
  };

  // All row updates below use the functional setRows form: the click that
  // dismisses an open payee/category popover commits its draft in the same
  // event sequence, so handlers must read the rows as of that commit rather
  // than their render closure, or they'd overwrite the just-committed edit.
  const handleUpdateRow = (id: number) => (row: PartialSplitRow) => {
    setRows((currentRows) => currentRows.map((r) => (r.id === id ? row : r)));
  };

  const handleRemoveRow = (id: number) => () => {
    setRows((currentRows) => currentRows.filter((r) => r.id !== id));
    setExpandedIds((current) => {
      const next = new Set(current);
      next.delete(id);
      return next;
    });
  };

  const handleAddRow = () => {
    const id = nextIdRef.current++;
    setRows((currentRows) => {
      const currentAllocated = currentRows
        .map((r) => r.amount.getOrElse(Money.zero(currency)))
        .reduce((a, b) => Money.plus(a, b), Money.zero(currency));
      const currentRemaining = Money.minus(total, currentAllocated);
      const newAmount = currentRemaining.isZero
        ? some(Money.zero(currency))
        : some(currentRemaining);
      return [...currentRows, emptyRow(id, newAmount, earliestDate)];
    });
    // A new split is there to be filled in, so open it right away.
    setExpandedIds((current) => new Set(current).add(id));
  };

  const handleCopyToAll = (id: number) => (field: CopyableField) => {
    setRows((currentRows) => {
      const source = currentRows.find((r) => r.id === id);
      if (!source) {
        return currentRows;
      }
      return currentRows.map((row) => {
        switch (field) {
          case 'payee':
            return {...row, payee: source.payee};
          case 'category':
            return {...row, category: source.category};
          case 'description':
            return {...row, description: source.description};
          case 'tagsAndFields':
            // Copy fresh clones so later edits to one split don't mutate the
            // arrays/objects shared into its siblings.
            return {
              ...row,
              tags: [...source.tags],
              fieldValues: {...source.fieldValues},
            };
        }
      });
    });
  };

  // Divides the total into n parts, giving the leftover cents to the first
  // `total % n` parts so the amounts always sum back to the total.
  const evenAmount = (i: number, n: number): Money => {
    // Distribute in the currency's display unit (whole yen for JPY, cents
    // for USD) so generated splits render exactly and survive re-editing —
    // ¥100 across three rows is ¥34/¥33/¥33, never sub-yen fractions. Any
    // sub-unit residue in the total itself (legacy data) lands on row 0 so
    // the rows still sum to the total.
    const unit = displayUnitHundredthsWithUnits(currency, unitsById);
    const totalUnits = Math.floor(total.amount / unit);
    const residue = total.amount - totalUnits * unit;
    const baseUnits = Math.floor(totalUnits / n);
    const remainderUnits = totalUnits % n;
    const units = i < remainderUnits ? baseUnits + 1 : baseUnits;
    return Money.fromInt(units * unit + (i === 0 ? residue : 0), currency);
  };

  const handleSplitEvenly = () => {
    setRows((currentRows) =>
      currentRows.map((r, i) => ({
        ...r,
        amount: some(evenAmount(i, currentRows.length)),
      })),
    );
  };

  // Shortcut for annual charges paid up front: 12 even splits, each dated one
  // month after the previous. Anchored on the first split's date, so setting
  // that date before clicking chooses where the schedule starts.
  const handleSplitMonthly = () => {
    // Reserve ids for all potential new rows up front so the state updater
    // stays pure (safe under double-invocation).
    const baseId = nextIdRef.current;
    nextIdRef.current += 12;
    setRows((currentRows) => {
      const n = 12;
      // Rows can be empty (all splits deleted), so fall back to the
      // transaction's earliest date rather than dereferencing a missing row.
      const anchor = currentRows[0]?.date ?? earliestDate;
      return ArrayUtils.range(n).map((i) => ({
        ...(currentRows[i] ?? emptyRow(baseId + i, none, anchor)),
        amount: some(evenAmount(i, n)),
        date: anchor.plus({months: i}),
      }));
    });
  };

  const footer = (
    <>
      <Button variant="outline" onClick={onRequestClose}>
        Cancel
      </Button>
      <Button variant="primary" type="submit" form={formId} disabled={!canSubmit}>
        Split
      </Button>
    </>
  );

  return (
    <Modal
      header="Split into transactions"
      onRequestClose={onRequestClose}
      size="lg"
      footer={footer}
    >
      <form id={formId} onSubmit={handleSubmit} className="flex flex-col gap-4">
        {rawTransactions.length === 1 ? (
          <div className="text-muted-foreground text-sm">
            {rawTransactions[0].description} &middot;{' '}
            {formatMoneyWithUnits(total, unitsById)}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <div className="border-border max-h-32 overflow-y-auto rounded-md border p-2">
              {rawTransactions.map((raw) => (
                <div
                  key={raw._id}
                  className="text-muted-foreground flex justify-between gap-4 text-sm"
                >
                  <span className="truncate">{raw.description}</span>
                  <span className="shrink-0">
                    {formatMoneyWithUnits(
                      Money.fromInt(raw.amount, currency),
                      unitsById,
                    )}
                  </span>
                </div>
              ))}
            </div>
            <div className="text-muted-foreground text-sm">
              Net {formatMoneyWithUnits(netTotal, unitsById)} &middot; each split
              consumes every raw transaction proportionally
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className={`text-sm font-medium ${remainingColorClass}`}>
            {rows.length} splits &middot;{' '}
            {formatMoneyWithUnits(remaining, unitsById)} remaining
            {rows.length < 2 && !unsplittableResidue && (
              <span className="text-muted-foreground">
                {' '}
                &middot; add at least 2 to split
              </span>
            )}
            {unsplittableResidue && (
              <span className="text-destructive">
                {' '}
                &middot; this {currencyLabelWithUnits(currency, unitsById)}{' '}
                amount includes a fraction of a{' '}
                {currencyLabelWithUnits(currency, unitsById)} unit and cannot
                be split
              </span>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              type="button"
              onClick={handleSplitEvenly}
              disabled={unsplittableResidue}
            >
              Split evenly
            </Button>
            <Button
              variant="outline"
              type="button"
              onClick={handleSplitMonthly}
              disabled={unsplittableResidue}
            >
              12 monthly
            </Button>
          </div>
        </div>
        <div className="flex flex-col gap-3">
          {rows.map((row, i) => (
            <SplitRowCard
              key={row.id}
              index={i}
              value={row}
              currency={currency}
              expanded={expandedIds.has(row.id)}
              onToggle={handleToggleRow(row.id)}
              onChange={handleUpdateRow(row.id)}
              onCopyToAll={handleCopyToAll(row.id)}
              onDelete={handleRemoveRow(row.id)}
            />
          ))}
          {/* Full-width so it reads as the placeholder for the next split card
              it will create, sitting where that card will appear. */}
          <Button
            variant="outline"
            type="button"
            onClick={handleAddRow}
            className="text-muted-foreground w-full gap-2 border-dashed shadow-none"
          >
            <Plus className="size-4" />
            Add split
          </Button>
        </div>
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={pending}
            onChange={(e) => setPending(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300"
          />
          <span className="text-foreground text-sm">
            Mark as pending (not finalized)
          </span>
        </label>
      </form>
    </Modal>
  );
};
